import type { Vec3, WeaponId } from '@game/shared';

/**
 * All game audio, synthesised at runtime through the Web Audio API.
 *
 * Nothing is loaded from disk: there are no audio assets in this repo and there
 * will not be until there is a licensing story for them, so every sound here is
 * built from noise bursts and oscillators. That also means zero network cost, no
 * decode hitch on the first shot, and per-weapon character that can be tuned by
 * editing numbers rather than re-cutting samples.
 *
 * Like the renderer, this is plain TypeScript driven by the game loop. React
 * never touches it.
 */

/** Per-weapon voicing. Rough physical intent, tuned by ear. */
interface ShotVoice {
  /** Length of the noise crack, in seconds. */
  crackMs: number;
  /** Band the crack lives in. */
  filterHz: number;
  filterQ: number;
  /** Low-frequency body thump: starting pitch and decay. */
  bodyHz: number;
  bodyMs: number;
  /** Ringing tail, for big-bore weapons. */
  tailMs: number;
  gain: number;
}

const SHOT_VOICES: Record<WeaponId, ShotVoice> = {
  smg: { crackMs: 70, filterHz: 1900, filterQ: 0.9, bodyHz: 150, bodyMs: 45, tailMs: 0, gain: 0.5 },
  rifle: { crackMs: 110, filterHz: 1400, filterQ: 1.1, bodyHz: 110, bodyMs: 80, tailMs: 90, gain: 0.72 },
  shotgun: { crackMs: 190, filterHz: 900, filterQ: 0.7, bodyHz: 80, bodyMs: 130, tailMs: 160, gain: 0.95 },
  sniper: { crackMs: 140, filterHz: 2600, filterQ: 1.6, bodyHz: 95, bodyMs: 110, tailMs: 420, gain: 1 },
};

/** How far a sound carries. Beyond this it is inaudible and skipped outright. */
const MAX_AUDIBLE_DISTANCE = 90;

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;

  /** Listener pose, updated once per frame from the camera. */
  private readonly listener = { x: 0, y: 0, z: 0, rightX: 1, rightZ: 0 };

  /** Owned by the settings store; this is only the applied value. */
  private volume = 0.7;
  private footstepAccumulator = 0;
  private footstepToggle = false;

  /**
   * Browsers refuse to start an AudioContext without a user gesture, so this is
   * called from the click that starts the game and again whenever the context is
   * found suspended (tab switches suspend it).
   */
  resume(): void {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.volume;
      this.master.connect(this.ctx.destination);
      this.noise = this.buildNoise(this.ctx);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.master) this.master.gain.value = this.volume;
  }

  /** Two seconds of white noise, reused by every noise-based sound. */
  private buildNoise(ctx: AudioContext): AudioBuffer {
    const buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    return buffer;
  }

  /** @param yaw the camera yaw, for stereo placement of world sounds. */
  setListener(position: Vec3, yaw: number): void {
    this.listener.x = position.x;
    this.listener.y = position.y;
    this.listener.z = position.z;
    // Right-hand vector for a yaw where forward is (-sin, 0, -cos).
    this.listener.rightX = Math.cos(yaw);
    this.listener.rightZ = -Math.sin(yaw);
  }

  /**
   * Distance attenuation and stereo placement for a world-space sound. Returns
   * null when the source is too far away to bother synthesising.
   */
  private place(at: Vec3 | null): { gain: number; pan: number; muffle: number } | null {
    if (!at) return { gain: 1, pan: 0, muffle: 20_000 };
    const dx = at.x - this.listener.x;
    const dy = at.y - this.listener.y;
    const dz = at.z - this.listener.z;
    const distance = Math.hypot(dx, dy, dz);
    if (distance > MAX_AUDIBLE_DISTANCE) return null;

    // Gentler than inverse-square: arena shooters need distant fights audible.
    const gain = 1 / (1 + distance / 7);
    const inverse = distance > 0.001 ? 1 / distance : 0;
    const pan = Math.max(
      -1,
      Math.min(1, (dx * this.listener.rightX + dz * this.listener.rightZ) * inverse),
    );
    // Air absorption: distant shots lose their high end, which is most of what
    // makes them read as distant.
    const muffle = Math.max(700, 18_000 - distance * 190);
    return { gain, pan, muffle };
  }

  /** Shared output chain for one voice: gain -> pan -> master. */
  private channel(gain: number, pan: number): GainNode | null {
    const ctx = this.ctx;
    if (!ctx || !this.master) return null;
    const amp = ctx.createGain();
    amp.gain.value = gain;
    const panner = ctx.createStereoPanner();
    panner.pan.value = pan;
    amp.connect(panner).connect(this.master);
    return amp;
  }

  /** A filtered burst of the shared noise buffer with an exponential decay. */
  private burst(
    destination: AudioNode,
    startAt: number,
    durationMs: number,
    filterType: BiquadFilterType,
    frequency: number,
    q: number,
    peak: number,
  ): void {
    const ctx = this.ctx;
    if (!ctx || !this.noise) return;
    const duration = durationMs / 1000;

    const source = ctx.createBufferSource();
    source.buffer = this.noise;
    // Random offset so repeated shots never phase-lock into a buzz.
    const offset = Math.random() * (this.noise.duration - duration - 0.01);

    const filter = ctx.createBiquadFilter();
    filter.type = filterType;
    filter.frequency.value = frequency;
    filter.Q.value = q;

    const envelope = ctx.createGain();
    envelope.gain.setValueAtTime(peak, startAt);
    envelope.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

    source.connect(filter).connect(envelope).connect(destination);
    source.start(startAt, Math.max(0, offset), duration);
    source.stop(startAt + duration);
  }

  /** A pitched blip with an exponential decay. */
  private tone(
    destination: AudioNode,
    startAt: number,
    durationMs: number,
    fromHz: number,
    toHz: number,
    peak: number,
    type: OscillatorType = 'sine',
  ): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const duration = durationMs / 1000;

    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(fromHz, startAt);
    if (toHz !== fromHz) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, toHz), startAt + duration);
    }

    const envelope = ctx.createGain();
    envelope.gain.setValueAtTime(0.0001, startAt);
    envelope.gain.exponentialRampToValueAtTime(peak, startAt + 0.004);
    envelope.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

    osc.connect(envelope).connect(destination);
    osc.start(startAt);
    osc.stop(startAt + duration);
  }

  // -------------------------------------------------------------------------
  // Game sounds
  // -------------------------------------------------------------------------

  /** @param at world position, or null for the local player's own weapon. */
  shot(weapon: WeaponId, at: Vec3 | null = null): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const placement = this.place(at);
    if (!placement) return;

    const voice = SHOT_VOICES[weapon];
    // Own weapon sits forward and dry; someone else's is placed and attenuated.
    const channel = this.channel(placement.gain * voice.gain, placement.pan);
    if (!channel) return;

    const now = ctx.currentTime;
    const cutoff = Math.min(voice.filterHz, placement.muffle);

    this.burst(channel, now, voice.crackMs, 'bandpass', cutoff, voice.filterQ, 0.9);
    // Transient click that gives the shot its attack.
    this.burst(channel, now, 12, 'highpass', Math.min(5000, placement.muffle), 0.5, 0.7);
    this.tone(channel, now, voice.bodyMs, voice.bodyHz, voice.bodyHz * 0.45, 0.55);
    if (voice.tailMs > 0) {
      this.burst(channel, now + 0.01, voice.tailMs, 'lowpass', cutoff * 0.5, 0.7, 0.18);
    }
  }

  /** Bullet striking geometry. */
  impact(at: Vec3): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const placement = this.place(at);
    if (!placement) return;
    const channel = this.channel(placement.gain * 0.5, placement.pan);
    if (!channel) return;

    this.burst(
      channel,
      ctx.currentTime,
      55,
      'bandpass',
      Math.min(2600 + Math.random() * 1800, placement.muffle),
      1.4,
      0.8,
    );
  }

  /** Confirmation that the local player's shot connected. */
  hitMarker(killed: boolean, headshot = false): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const channel = this.channel(0.45, 0);
    if (!channel) return;
    const now = ctx.currentTime;
    if (killed) {
      this.tone(channel, now, 80, 1200, 1600, 0.6, 'square');
      this.tone(channel, now + 0.07, 160, 1600, 2200, 0.5, 'square');
    } else {
      this.tone(channel, now, 60, 1500, 1100, 0.5, 'triangle');
    }
    // A bright extra ping on top: the unmistakable "that was the head" chime.
    if (headshot) {
      this.tone(channel, now, 50, 2600, 3400, 0.45, 'sine');
      this.tone(channel, now + 0.05, 70, 3200, 2800, 0.35, 'sine');
    }
  }

  /** Taking damage — a dull thud, deliberately not directional. */
  damage(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const channel = this.channel(0.6, 0);
    if (!channel) return;
    const now = ctx.currentTime;
    this.tone(channel, now, 180, 220, 70, 0.7);
    this.burst(channel, now, 90, 'lowpass', 700, 0.7, 0.35);
  }

  death(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const channel = this.channel(0.7, 0);
    if (!channel) return;
    const now = ctx.currentTime;
    this.tone(channel, now, 700, 420, 60, 0.6, 'sawtooth');
  }

  respawn(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const channel = this.channel(0.5, 0);
    if (!channel) return;
    const now = ctx.currentTime;
    this.tone(channel, now, 90, 420, 700, 0.4);
    this.tone(channel, now + 0.08, 160, 700, 1050, 0.4);
  }

  /** Magazine out / magazine in, spaced across the weapon's reload time. */
  reload(reloadMs: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const channel = this.channel(0.4, 0);
    if (!channel) return;
    const now = ctx.currentTime;
    const duration = reloadMs / 1000;
    this.burst(channel, now + duration * 0.15, 40, 'bandpass', 2400, 2, 0.5);
    this.burst(channel, now + duration * 0.62, 50, 'bandpass', 1600, 2, 0.6);
    this.burst(channel, now + duration * 0.92, 35, 'highpass', 3200, 1, 0.5);
  }

  weaponSwitch(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const channel = this.channel(0.35, 0);
    if (!channel) return;
    this.burst(channel, ctx.currentTime, 45, 'bandpass', 2000, 2.2, 0.6);
  }

  /**
   * Footsteps are driven by distance travelled rather than a timer, so they stay
   * in step with the player at any speed. Called every frame while grounded.
   */
  stride(distance: number, sprinting: boolean): void {
    const ctx = this.ctx;
    if (!ctx) return;
    this.footstepAccumulator += distance;
    const strideLength = sprinting ? 2.2 : 1.7;
    if (this.footstepAccumulator < strideLength) return;
    this.footstepAccumulator = 0;
    this.footstepToggle = !this.footstepToggle;

    const channel = this.channel(sprinting ? 0.22 : 0.15, this.footstepToggle ? -0.2 : 0.2);
    if (!channel) return;
    this.burst(channel, ctx.currentTime, 55, 'lowpass', 700 + Math.random() * 250, 0.8, 0.7);
  }

  /** Someone else's footstep — the most tactically important sound in the game. */
  remoteStep(at: Vec3): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const placement = this.place(at);
    if (!placement) return;
    const channel = this.channel(placement.gain * 0.5, placement.pan);
    if (!channel) return;
    this.burst(
      channel,
      ctx.currentTime,
      60,
      'lowpass',
      Math.min(900, placement.muffle),
      0.8,
      0.7,
    );
  }

  jump(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const channel = this.channel(0.18, 0);
    if (!channel) return;
    this.burst(channel, ctx.currentTime, 40, 'lowpass', 900, 0.7, 0.5);
  }

  land(hard: boolean): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const channel = this.channel(hard ? 0.4 : 0.2, 0);
    if (!channel) return;
    const now = ctx.currentTime;
    this.burst(channel, now, hard ? 120 : 70, 'lowpass', hard ? 500 : 800, 0.8, 0.8);
    if (hard) this.tone(channel, now, 130, 120, 60, 0.5);
  }

  // -------------------------------------------------------------------------
  // Interface and match flow
  // -------------------------------------------------------------------------

  uiSelect(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const channel = this.channel(0.3, 0);
    if (!channel) return;
    this.tone(channel, ctx.currentTime, 70, 660, 990, 0.5, 'triangle');
  }

  /** One beep per second of the pre-round countdown; the last one is higher. */
  countdown(final: boolean): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const channel = this.channel(0.35, 0);
    if (!channel) return;
    this.tone(channel, ctx.currentTime, final ? 260 : 110, final ? 880 : 560, final ? 880 : 560, 0.5, 'square');
  }

  roundStart(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const channel = this.channel(0.5, 0);
    if (!channel) return;
    const now = ctx.currentTime;
    this.tone(channel, now, 140, 440, 440, 0.5, 'square');
    this.tone(channel, now + 0.13, 140, 660, 660, 0.5, 'square');
    this.tone(channel, now + 0.26, 320, 880, 880, 0.55, 'square');
  }

  roundEnd(won: boolean): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const channel = this.channel(0.5, 0);
    if (!channel) return;
    const now = ctx.currentTime;
    const notes = won ? [523, 659, 784] : [523, 440, 349];
    notes.forEach((hz, index) => {
      this.tone(channel, now + index * 0.16, 300, hz, hz, 0.45, 'triangle');
    });
  }

  dispose(): void {
    void this.ctx?.close();
    this.ctx = null;
    this.master = null;
    this.noise = null;
  }
}
